import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { Observable } from 'rxjs';
import { UserFavorite, UserFavoriteCollection } from '../_models/userFavorite';
import { map } from 'rxjs/operators';
import { Crush, CrushCollection } from '../_models/crush';
import { User, UserCollection } from '../_models';
import { Visit, VisitCollection } from '../_models/visit';
import { PictureAccessRequest, PictureAccessRequestCollection } from '../_models/pictureAccessRequest';
import { ModelCollection } from '../_models/hydra';
import { UserFavoriteRoom, UserFavoriteRoomCollection } from '../_models/userFavoriteRoom';

@Injectable()
export class UserService {

    public countryPhonesFormat: any[] = [
        {
            callingCode: '+33',
            placeholder: "XX XX XX XX XX",
            regex: new RegExp(/^06|07/)
        },    
        {
            callingCode: '+32',
            placeholder: "XXXX XX XX XX",
            regex: new RegExp(/^04|0495/)
        },    
        {
            callingCode: '+41',
            placeholder: "XXX XXX XXXX",
            regex: new RegExp(/^075|076|077|078|079/)
        },    
        {
            callingCode: '+49', 
            placeholder: "XXXXX XXXXXXX",
            regex: new RegExp(/^01|02/)
        },    
        {
            callingCode: '+376', 
            placeholder: "XX XX XXXXX",
            regex: new RegExp(/^[0-9]/)
        },    
        {
            callingCode: '+1',   
            placeholder: "XXX XXX XXXX",
            regex: new RegExp(/^[0-9]/)
        },    
        {
            callingCode: '+45',   
            placeholder: "XX XX XX XX",
            regex: new RegExp(/^20|31|40|42|50|53|60|61|71|81|91|93/)
        },    
        {
            callingCode: '+34',
            placeholder: "XXX XX XX XX",
            regex: new RegExp(/^7|6/)
        },    
        {
            callingCode: '+590',   
            placeholder: "XXXX XX XX XX",
            regex: new RegExp(/^0690|0691/)
        },
        {
            callingCode: '+30',   
            placeholder: "XX XXXX XXXX",
            regex: new RegExp(/^6/)
        },    
        {
            callingCode: '+594',   
            placeholder: "XXX XX XX XX",
            regex: new RegExp(/^694/)
        },
        {
            callingCode: '+353',
            placeholder: "(XX) XXX XXXX",
            regex: new RegExp(/^083|085|086|087|089/)
        },
        {
            callingCode: '+39',
            placeholder: "XXX XXX XXXX",
            regex: new RegExp(/^3/)
        },
        {
            callingCode: '+423',   
            placeholder: "XXX XX XX",
            regex: new RegExp(/^639|692|693/)
        },    
        {
            callingCode: '+352',
            placeholder: "XXX XXX XXX",
            regex: new RegExp(/^6[2569]1/)
        },    
        {
            callingCode: '+212',   
            placeholder: "XXX-XXXXXX",
            regex: new RegExp(/^[0-9]/)
        },    
        {
            callingCode: '+596',   
            phoneMask: "XXXX XX XX XX",
            regex: new RegExp(/^696|697/)
        },    
        {
            callingCode: '+262',   
            placeholder: "XXX XX XX XX",
            regex: new RegExp(/^639/)
        },    
        {
            callingCode: '+377',   
            placeholder: "XX XX XX XX",
            regex: new RegExp(/^[0-9]/)
        },    
        {
            callingCode: '+687',   
            placeholder: "XX.XX.XX",
            regex: new RegExp(/^[0-9]/)
        },    
        {
            callingCode: '+31',   
            placeholder: "XX XXXXXXXX",
            regex: new RegExp(/^06/)
        },    
        {
            callingCode: '+689',   
            placeholder: "XX XX XX XX",
            regex: new RegExp(/^87|89/)
        },
        {
            callingCode: '+351',
            placeholder: "XXX XXX XXX",
            regex: new RegExp(/^[0-9]/)
        },    
        {
            callingCode: '+262',   
            placeholder: "XXX XX XX XX",
            regex: new RegExp(/^639|692|693/)
        },    
        {
            callingCode: '+590',   
            placeholder: "XXXX XX XX XX",
            regex: new RegExp(/^0690|0691/)
        },    
        {
            callingCode: '+590',   
            placeholder: "XXXX XX XX XX",
            regex: new RegExp(/^0690|0691/)
        },    
        {
            callingCode: '+508',
            placeholder: "XX XX XX",
            regex: new RegExp(/^55/)
        },    
        {
            callingCode: '+44',
            placeholder: "XXX XXXX XXXX",
            regex: new RegExp(/^07/)    
        },    
        {
            callingCode: '+216',
            placeholder: "XX XXX XXX",
            regex: new RegExp(/^[0-9]/)
        },    
        {
            callingCode: '+681',   
            placeholder: "XX XX XX",
            regex: new RegExp(/^4|8/)
        } 
    ];

    public CRITERIA_AGE = 'age';
    public CRITERIA_DISTANCE = 'distance';
    public CRITERIA_CHILDREN = 'children';
    public CRITERIA_SILHOUETTE = 'silhouette';
    public CRITERIA_HEIGHT = 'height';
    public CRITERIA_REGISTER = 'register';

    public SUGGESTIONS_CRITERIA = [
        this.CRITERIA_AGE,
        this.CRITERIA_DISTANCE,
        this.CRITERIA_CHILDREN,
        this.CRITERIA_SILHOUETTE,
        this.CRITERIA_REGISTER,
        this.CRITERIA_HEIGHT,
    ];

    /**
     * Constructor
     */
    constructor(
        private apiService: ApiService
    ) {
    }

    /**
     * Get current online users
     */
    public getOnlines(users: Array<string>, features: Array<string> = ['global']) {
        return this.apiService.getOnlines(users, features);
    }

    /**
     * Get current user favorites
     */
     public getFavorites(count: number, page: number = 1, partial: boolean = false): Observable<{ items: Array<UserFavorite>; totalItems: number }> {
        return this.apiService.getFavorites(count, page, partial).pipe(
            map((responseData: UserFavoriteCollection) => {
                let favs: Array<UserFavorite> = [];
                for (let fav of responseData['hydra:member']) {
                    favs.push(fav);
                }

                return { items: favs, totalItems: responseData['hydra:totalItems'] };
            })
        );
    }

    /**
     * Get current user favorite rooms
     */
     public getFavoriteRooms(count: number, page: number = 1, partial: boolean = false): Observable<{ items: Array<UserFavoriteRoom>; totalItems: number }> {
        return this.apiService.getFavoriteRooms(count, page, partial).pipe(
            map((responseData: UserFavoriteRoomCollection) => {
                let favs: Array<UserFavoriteRoom> = [];
                for (let fav of responseData['hydra:member']) {
                    favs.push(fav);
                }

                return { items: favs, totalItems: responseData['hydra:totalItems'] };
            })
        );
    }

    /**
     * Get current user sent crushes
     */
     public getSentCrushes(count: number, page: number = 1, partial: boolean = false): Observable<{ items: Array<Crush>; totalItems: number }> {
        return this.apiService.getSentCrushes(count, page, partial).pipe(
            map((responseData: CrushCollection) => {
                let crushes: Array<Crush> = [];
                for (let crush of responseData['hydra:member']) {
                    crushes.push(crush);
                }

                return { items: crushes, totalItems: responseData['hydra:totalItems'] };
            })
        );
    }

     /**
     * Get current user received crushes
     */
    public getReceivedCrushes(count: number, page: number = 1, partial: boolean = false): Observable<{ items: Array<Crush>; totalItems: number }> {
        return this.apiService.getReceivedCrushes(count, page, partial).pipe(
            map((responseData: CrushCollection) => {
                let crushes: Array<Crush> = [];
                for (let crush of responseData['hydra:member']) {
                    crushes.push(crush);
                }

                return { items: crushes, totalItems: responseData['hydra:totalItems'] };
            })
        );
    }

    /**
     * Get current user accepted crushes
     */
    public getAcceptedCrushes(count: number, page: number = 1, partial: boolean = false): Observable<{ items: Array<Crush>; totalItems: number }> {
        return this.apiService.getAcceptedCrushes(count, page, partial).pipe(
            map((responseData: CrushCollection) => {
                let crushes: Array<Crush> = [];
                for (let crush of responseData['hydra:member']) {
                    crushes.push(crush);
                }

                return { items: crushes, totalItems: responseData['hydra:totalItems'] };
            })
        );
    }

    /**
     * Get current user sent or accepted crushes
     */
    public getSentorAcceptedCrushes(count: number, page: number = 1, partial: boolean = false): Observable<{ items: Array<Crush>; totalItems: number }> {
        return this.apiService.getSentorAcceptedCrushes(count, page, partial).pipe(
            map((responseData: CrushCollection) => {
                let crushes: Array<Crush> = [];
                for (let crush of responseData['hydra:member']) {
                    crushes.push(crush);
                }

                return { items: crushes, totalItems: responseData['hydra:totalItems'] };
            })
        );
    }

    /**
     * Get current user suggestions
     */
     public getSuggestions(count: number, page: number = 1, partial: boolean = false): Observable<{ items: Array<User>; totalItems: number }> {
        return this.apiService.getSuggestions(count, page, partial).pipe(
            map((responseData: UserCollection) => {
                let suggestions: Array<User> = [];
                for (let user of responseData['hydra:member']) {
                    suggestions.push(user);
                }

                return {
                    items: suggestions,
                    totalItems: responseData['hydra:totalItems']
                };
            })
        );
    }

    /**
     * Get current user new visits
     */
    public getNewVisits() {
        return this.apiService.getNewVisits().pipe(
            map((responseData: VisitCollection) => {
                let visits: Array<Visit> = [];
                for (let visit of responseData['hydra:member']) {
                    visits.push(visit);
                }

                return { items: visits, totalItems: responseData['hydra:totalItems'] };
            })
        );
    }

    /**
     * Get current user favorites
     */
    public getVisits(count: number, page: number = 1, partial: boolean = false): Observable<{ items: Array<User>; totalItems: number }> {
        return this.apiService.getVisits(count, page, partial).pipe(
            map((responseData: VisitCollection) => {
                let visits: Array<User> = [];
                for (let visit of responseData['hydra:member']) {
                    let current: User = visit.user;
                    current.lastVisit = new Date(visit.updatedAt);
                    visits.push(current);
                }

                return { items: visits, totalItems: responseData['hydra:totalItems'] };
            })
        );
    }

    /**
     * Get current newbies
     */
     public getNewbies(count: number, page: number = 1, partial: boolean = false): Observable<{ items: Array<User>; totalItems: number }> {
        return this.apiService.getNewbies(count, page, partial).pipe(
            map((responseData: UserCollection) => {
                let newbies: Array<User> = [];
                for (let user of responseData['hydra:member']) {
                    newbies.push(user);
                }

                return { items: newbies, totalItems: responseData['hydra:totalItems'] };
            })
        );
    }

    /**
     * Get online users
     */
     public getOnlineUsers(count: number, page: number = 1, partial: boolean = false): Observable<{ items: Array<User>; totalItems: number }> {
        return this.apiService.getOnlineUsers(count, page, partial).pipe(
            map((responseData: UserCollection) => {
                let onlines: Array<User> = [];
                for (let user of responseData['hydra:member']) {
                    onlines.push(user);
                }

                return { items: onlines, totalItems: responseData['hydra:totalItems'] };
            })
        );
    }

    /**
     * Add user to favorites
     */
    public addToFavorites(user: User): Observable<User> {
        return this.apiService.toggleFavorites(user['@id']).pipe(
            map((responseData: User) => {
                return responseData;
            })
        );
    }

    /**
     * Remove user from favorites
     */
    public removeFromFavorites(user: User) {
        return this.apiService.toggleFavorites(user['@id']).pipe(
            map((responseData: User) => {
                return responseData;
            })
        );
    }

    /**
     * Send crush
     */
    public sendCrush(user: User): Observable<Crush> {
        return this.apiService.toggleCrush(user['@id']).pipe(
            map((responseData: Crush) => {
                return responseData;
            })
        );
    }

    /**
     * Remove crush
     */
    public removeCrush(user: User): Observable<Crush> {
        return this.apiService.toggleCrush(user['@id']).pipe(
            map((responseData: Crush) => {
                return responseData;
            })
        );
    }

    /**
     * Get pending album authorisations
     */
    public getPendingAuthorisations(count: number = 20, page: number = 1): Observable<{ items: Array<PictureAccessRequest>; totalItems: number }> {
        return this.apiService.getPendingAuthorisations(count, page).pipe(
            map((responseData: PictureAccessRequestCollection) => {
                let requests: Array<PictureAccessRequest> = [];
                for (let request of responseData['hydra:member']) {
                    requests.push(request);
                }

                return { items: requests, totalItems: responseData['hydra:totalItems'] };
            })
        );
    }

    /**
     * Delete album authorisation
     */
    public deletePictureAccessRequest(requestId: string): Observable<void> {
        return this.apiService.deletePictureAccessRequest(requestId);
    }

    /**
     * Get locked users
     */
     public getLocked(): Observable<{ items: Array<User>; totalItems: number }> {
        return this.apiService.getLocked().pipe(
            map((responseData: ModelCollection<User>) => {
                let requests: Array<User> = [];
                for (let request of responseData['hydra:member']) {
                    requests.push(request);
                }

                return { items: requests, totalItems: responseData['hydra:totalItems'] };
            })
        );
    }

    /**
     * Delete
     */
     public delete(userId:string, reason:string): Observable<void> {
        return this.apiService.delete(userId, reason);
    }

    /**
     * Suspend
     */
    public suspend(userId:string, reason:string): Observable<void> {
        return this.apiService.suspend(userId, reason);
    }

    /**
     * Suspend
     */
    public validatePhone(phone:string, prefix:string): boolean {
        return true;
    }
}
